import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Eye, EyeOff, Lock, Pencil, X } from "lucide-react";
import { STOCK_STATUSES } from "@/data/content";
import { Reveal } from "@/components/Motion";

const FieldLabel = ({ children }) => (
  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">{children}</p>
);

const EditDrawer = ({ product, onClose, onSave }) => {
  const [draft, setDraft] = useState({ ...product });
  const inputCls =
    "w-full border border-[#2A2723] bg-[#0F0E0C] px-4 py-3 font-jakarta text-sm text-[#F1F1F1] focus:border-[#F4B032] focus:outline-none";

  return (
    <motion.div
      data-testid="admin-edit-drawer"
      data-lenis-prevent
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-y-0 right-0 z-40 w-full overflow-y-auto border-l border-[#2A2723] bg-[#0F0E0C] sm:w-[460px]"
    >
      <div className="flex items-center justify-between border-b border-[#2A2723] p-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">Editing · {product.code}</p>
          <h3 className="mt-1 font-condensed text-2xl font-extrabold uppercase text-[#F1F1F1]">{product.name}</h3>
        </div>
        <button data-testid="admin-edit-close-button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#2A2723] text-[#A59E96] transition-colors hover:border-[#F4B032] hover:text-[#F4B032]">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-7 p-6 pb-28">
        <div className="relative overflow-hidden border border-[#2A2723]">
          <img src={draft.image} alt={draft.name} className="h-44 w-full object-cover" />
          <button data-testid="admin-photo-replace-button" className="absolute bottom-3 right-3 rounded-full bg-black/70 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F1F1F1] backdrop-blur transition-colors hover:bg-[#F4B032] hover:text-[#0F0E0C]">
            Replace photo
          </button>
        </div>

        <div>
          <FieldLabel>Product name</FieldLabel>
          <input data-testid="admin-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={`${inputCls} mt-3`} />
        </div>

        <div>
          <FieldLabel>{draft.hasFootage ? "Base price per linear ft ($)" : "Base price each ($)"}</FieldLabel>
          <input data-testid="admin-price-input" type="number" min="0" value={draft.basePrice} onChange={(e) => setDraft({ ...draft, basePrice: Number(e.target.value) })} className={`${inputCls} mt-3`} />
        </div>

        <div>
          <FieldLabel>Stock status</FieldLabel>
          <div className="mt-3 flex flex-wrap gap-2">
            {STOCK_STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`admin-status-${s.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                onClick={() => setDraft({ ...draft, status: s })}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                  draft.status === s ? "border-[#F4B032] bg-[#F4B032] text-[#0F0E0C]" : "border-[#2A2723] text-[#A59E96] hover:border-[#A59E96]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between border border-[#2A2723] bg-[#181614] p-4">
          <div>
            <FieldLabel>Visible on website</FieldLabel>
            <p className="mt-1 font-jakarta text-xs text-[#A59E96]">Hidden products disappear from the catalog instantly</p>
          </div>
          <button
            data-testid="admin-visible-toggle"
            onClick={() => setDraft({ ...draft, visible: !draft.visible })}
            className={`flex h-8 w-14 items-center rounded-full p-1 transition-colors ${draft.visible ? "justify-end bg-[#F4B032]" : "justify-start bg-[#2A2723]"}`}
          >
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ${draft.visible ? "bg-[#0F0E0C] text-[#F4B032]" : "bg-[#A59E96] text-[#0F0E0C]"}`}>
              {draft.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </span>
          </button>
        </div>

        <button
          data-testid="admin-save-button"
          onClick={() => onSave(draft)}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F4B032] py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]"
        >
          <Check className="h-4 w-4" /> Save — Publish Live
        </button>
      </div>
    </motion.div>
  );
};

const StaffAdmin = ({ products, onSave, onViewCatalog }) => {
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);

  const visibleCount = products.filter((p) => p.visible !== false).length;
  const outCount = products.filter((p) => p.status === "Out of Stock").length;

  const save = (draft) => {
    onSave(products.map((p) => (p.id === draft.id ? draft : p)));
    setEditing(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 4000);
  };

  return (
    <div data-testid="staff-admin-page" className="min-h-screen bg-[#0F0E0C] pb-32">
      <div className="grain-overlay" />

      <header className="flex items-center justify-between border-b border-[#2A2723] px-6 py-5 md:px-12">
        <div className="flex items-center gap-4">
          <img src="/avo-logo.png" alt="AVO Fence & Supply" className="h-8 w-auto" data-testid="admin-logo" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">Staff Admin</span>
        </div>
        <span className="inline-flex items-center gap-2 border border-[#F4B032]/40 bg-[#F4B032]/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#F4B032]">
          <Lock className="h-3 w-3" /> Demo — changes go live instantly
        </span>
      </header>

      <section className="px-6 pb-8 pt-14 md:px-12">
        <Reveal>
          <h1 className="font-condensed text-4xl font-extrabold uppercase tracking-tight text-[#F1F1F1] md:text-6xl">
            Your catalog, your hands<span className="text-[#F4B032]">.</span>
          </h1>
          <p className="mt-4 max-w-xl font-jakarta text-sm leading-relaxed text-[#A59E96]">
            No developer needed. Your team edits prices, stock status and products right here — and
            every change appears on the live catalog the moment they hit save.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 grid grid-cols-3 gap-[2px] bg-[#2A2723]">
          {[
            [visibleCount, "Products live on site"],
            [products.length, "Styles & SKUs managed"],
            [outCount, "Out of stock right now"],
          ].map(([v, l]) => (
            <div key={l} className="bg-[#0F0E0C] p-5">
              <p className="font-condensed text-3xl font-extrabold text-[#F4B032] md:text-4xl">{v}</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#A59E96]">{l}</p>
            </div>
          ))}
        </Reveal>
      </section>

      <section className="px-6 md:px-12">
        <Reveal delay={0.15}>
          <div className="overflow-x-auto border border-[#2A2723]" data-lenis-prevent>
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-[#2A2723] bg-[#181614]">
                  {["Code", "Product", "Category", "Base Price", "Status", "On Site", ""].map((h, i) => (
                    <th key={i} className="px-5 py-3.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A59E96]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-b border-[#2A2723] transition-colors last:border-0 hover:bg-[#181614]">
                    <td className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4B032]">{p.code}</td>
                    <td className="px-5 py-4">
                      <span className="font-condensed text-lg font-bold uppercase text-[#F1F1F1]">{p.name}</span>
                    </td>
                    <td className="px-5 py-4 font-jakarta text-xs text-[#A59E96]">{p.category}</td>
                    <td className="px-5 py-4 font-mono text-sm text-[#F1F1F1]">
                      ${p.basePrice}<span className="text-[#A59E96]">{p.hasFootage ? "/ft" : " ea"}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-block rounded-full px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${
                        p.status === "In Stock" ? "bg-emerald-950/60 text-emerald-300" :
                        p.status === "Low Stock" ? "bg-[#F4B032]/15 text-[#F4B032]" :
                        "bg-red-950/60 text-red-300"
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {p.visible !== false
                        ? <Eye className="h-4 w-4 text-[#F4B032]" />
                        : <EyeOff className="h-4 w-4 text-[#A59E96]" />}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        data-testid={`admin-edit-${p.id}`}
                        onClick={() => setEditing(p)}
                        className="inline-flex items-center gap-2 rounded-full border border-[#2A2723] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F1F1F1] transition-colors hover:border-[#F4B032] hover:text-[#F4B032]"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

      </section>

      <AnimatePresence>
        {editing && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)} className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" />
            <EditDrawer product={editing} onClose={() => setEditing(null)} onSave={save} />
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {saved && (
          <motion.div
            data-testid="admin-saved-toast"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            className="fixed bottom-24 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-full border border-[#F4B032]/40 bg-[#0F0E0C] py-3 pl-5 pr-3 shadow-2xl"
          >
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4B032]">
              <Check className="h-4 w-4" /> Saved — live on the catalog
            </span>
            <button
              data-testid="admin-view-catalog-button"
              onClick={onViewCatalog}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F4B032] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]"
            >
              View it <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StaffAdmin;
